import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'

import Icon from 'components/atoms/icon'
import NewTooltip from 'components/atoms/new-tooltip'
import {
  getPriceEvaluationBlockCount,
  normalizePriceEvaluation,
  priceEvaluationColors,
  priceEvaluationSteps,
} from 'utilities/price-evaluation'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 0;
  gap: ${({ theme }) => theme.sizeByFactor(0.5)};
  font-family: ${({ theme }) => theme.font};
  color: ${({ theme }) => theme.colors.text};
`

const Label = styled.span`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${({ theme }) => theme.baseFontSize};
  font-weight: ${({ theme }) => theme.baseFontWeight};
  line-height: ${({ theme }) => theme.baseLineHeight};
  white-space: nowrap;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizeByFactor(0.375)};
`

const Blocks = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizeByFactor(0.375)};
`

const Block = styled.span`
  display: block;
  width: ${({ theme }) => theme.sizeByFactor(1.75)};
  height: ${({ theme }) => theme.sizings.lvl1};
  background-color: ${({ theme, $color }) =>
    theme.colors[$color] || theme.colors.priceEvaluationNone};
`

/** The five blocks of an evaluation; the better the price, the more are coloured */
export const PriceEvaluationBlocks = ({
  evaluation,
  className,
  ...restProps
}) => {
  const step = normalizePriceEvaluation(evaluation)
  const activeBlocks = getPriceEvaluationBlockCount(step)
  const color = priceEvaluationColors[step]

  return (
    <Blocks className={className} {...restProps}>
      {priceEvaluationSteps.map((_, index) => (
        <Block
          key={index.toString()}
          $color={index < activeBlocks ? color : undefined}
        />
      ))}
    </Blocks>
  )
}

PriceEvaluationBlocks.propTypes = {
  evaluation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
}

PriceEvaluationBlocks.defaultProps = {
  evaluation: null,
  className: null,
}

export const InfoButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.sizings.lvl2};
  height: ${({ theme }) => theme.sizings.lvl2};
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
`

export const InfoIcon = () => (
  <Icon type="info" color="text" width="13" height="13" />
)

/**
 * Shows the pricing evaluation of a car as 5 blocks. The better the price, the
 * more blocks are coloured. Without an evaluation all blocks are grey.
 */
const PriceEvaluationIndicator = ({
  evaluation,
  onInfoClick,
  showLabel,
  className,
  ...restProps
}) => {
  const { t } = useTranslation()
  const step = normalizePriceEvaluation(evaluation)
  const label = t(`priceEvaluation.${step || 'none'}`)
  const infoLabel = t('priceEvaluation.infoTooltip')

  // The indicator sits inside the card link: only open the modal, don't open the car file
  const handleInfoClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    onInfoClick && onInfoClick(event)
  }

  return (
    <Container className={className} {...restProps}>
      {showLabel && <Label>{label}</Label>}
      <Row>
        <PriceEvaluationBlocks evaluation={step} aria-label={label} />
        <NewTooltip
          title={infoLabel}
          placement="top"
          arrow
          enterTouchDelay={0}
          leaveTouchDelay={3000}
        >
          <InfoButton
            type="button"
            onClick={handleInfoClick}
            aria-label={infoLabel}
          >
            <InfoIcon />
          </InfoButton>
        </NewTooltip>
      </Row>
    </Container>
  )
}

PriceEvaluationIndicator.propTypes = {
  /** A PriceEvaluationEnum value or the 1-based step number (1 = very good price) */
  evaluation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onInfoClick: PropTypes.func,
  showLabel: PropTypes.bool,
  className: PropTypes.string,
}

PriceEvaluationIndicator.defaultProps = {
  evaluation: null,
  onInfoClick: null,
  showLabel: true,
  className: null,
}

export default PriceEvaluationIndicator
